import { ActionContext } from 'vuex';

import {
  buy,
  renew,
  fetchOrder,
  getContractById,
  fetchUpsell,
  applyOfferCoupon as applyOfferCouponRemotely,
  buyUpsell as buyUpsellRemotely,
  renewUpsell,
} from '@/api/resources/checkout';

import { Order } from '@/classes/Checkout/Order';
import { Purchase } from '@/classes/Purchase';
import { UpsellPurchase } from '@/classes/UpsellPurchase';
import { Upsell } from '@/classes/Checkout/Upsell';
import { TargetContentLink } from '@/classes/Checkout/TargetContentLink';
import TraceableError from '@/classes/TraceableError';

import { ICheckout, ICheckoutCart, ICheckoutCartOffer, ICheckoutCartOfferPrice, ICheckoutStep } from '@/interfaces/Checkout';
import { IPurchaseResponse, IPurchase } from '@/interfaces/Purchase';
import { IPaymentPurchase } from '@/interfaces/Payment';
import { IOrder, IOrderDetailsResponse } from '@/interfaces/Order';
import { IContractDetailsResponse } from '@/interfaces/Contract';
import ICheckoutState from '@/interfaces/CheckoutState';
import { IUpsell } from '@/interfaces/Upsell';

import {
  EPaymentMethod,
  EEligibilityAction,
  EErrorOriginTransform,
  EErrorExhibition,
  EErrorType,
  EErrorSeverity,
  EErrorScope,
  EPaymentMethodTypes,
  EErrorOrigin
} from '@/helpers/enums';
import { redirectToSuccessPage } from '@/helpers/checkout';
import {
  pushOrderConfirmedToDatalayer,
  pushOrderUpsellConfirmedToDatalayer,
  pushOrderBankslipGeneratedToDatalayer,
  pushOrderUpsellBankslipGeneratedToDatalayer,
  pushRefusalUpsellLoadedToDatalayer,
  pushCheckoutEverywhereOrderConfirmedToDatalayer,
  purshOrderAwaitingPaymentToDatalayer,
} from '@/helpers/dataLayer';
import { addError, getErrorByCodeAndType, getDefaultErrorByType } from '@/helpers/errors';
import { Payment } from '@/classes/Checkout/Payment';
import { Offer } from '@/classes/Checkout/Offer';
import { sendInteractEvent } from '@/helpers/events';
import { EInteractEventTrackerAction } from '../../InteractEventTracker/enums/EInteractEventTracker';
import Cookie from '@/helpers/cookie';

import { removeScriptKoin } from '@/helpers/dom';

const root = { root: true };

let counter = 0;

function getCookieEmpU(): Cookie {
  return new Cookie({ name: 'COOKIE_EMP_U' }).getSession().value;
}

export default {
  async applyOfferCoupon({ commit, state }: ActionContext<ICheckoutState, any>, coupon: string | null) {
    const cart: ICheckoutCart = state.checkout.cart;

    if (cart && cart.uuid) {
      try {
        commit('setPaymentLoading', true);

        const payload = { coupon };
        const request = await applyOfferCouponRemotely(cart.uuid, payload);
        const checkout: ICheckout = request.data;

        if ([2000, 2001].includes(Number(checkout.cart.offer!.error_code))) {
          throw { code: String(checkout.cart.offer!.error_code) };
        }

        commit('updateCheckout', checkout);
        commit('setSelectedOfferCoupon', coupon);

        return checkout;
      } catch (error) {

        throw error;
      } finally {
        commit('setPaymentLoading', false);
      }
    }
  },
  async buyCE({ dispatch }: ActionContext<ICheckoutState, any>, purchasePayload: any) {
    try {
      const purchaseRequest = await buy({ ...purchasePayload.payload, sales_channel: 'checkout-everywhere'});

      const purchaseResponse: IPurchaseResponse = purchaseRequest.data;

      if (purchaseResponse.error && purchaseResponse.error.code) {
        throw { response: purchaseRequest };
      }

      const orderResponse = purchaseResponse.order && await dispatch('resolveOrderUpsell', purchaseResponse.order.id);

      const { order } = purchaseResponse;

      pushCheckoutEverywhereOrderConfirmedToDatalayer(order, purchasePayload.toDataLayer.offerRoot, purchasePayload.toDataLayer.offerSelected);

      return {
        response: {
          ...purchaseResponse,
        },
        order: {
          ...orderResponse,
        }
      };
    } catch (error) {
      if (error.response) {
        return error.response;
      }

      return error;
    }
  },
  async buy({ state, commit, getters, dispatch, rootGetters }: ActionContext<ICheckoutState, any>) {
    dispatch('App/toggleLoader', true, root);
    dispatch('setPaymentLoading', true);

    if (!getters.isRenewal && getters.customTemplateGuid) {
      const el = document.querySelector('.copy-step.checkout');
      if (el) {
        el.scrollIntoView(false);
      }
    } else {
      window.scrollTo(0, 0);
    }

    const checkout: ICheckout = state.checkout;
    const cart: ICheckoutCart = checkout.cart;
    const rootOffer: ICheckoutCartOffer = getters.rootOffer;
    const selectedOffer: ICheckoutCartOffer = getters.selectedOffer;
    const selectedOfferPrice: ICheckoutCartOfferPrice = getters.selectedOfferPrice;
    const payment: Payment = selectedOffer.payment;
    const paymentMethodSelected: EPaymentMethod = payment.selectedPaymentMethod;
    const selectedMethod = payment.getPaymentMethod(paymentMethodSelected);
    const eligibleAction: EEligibilityAction = selectedOffer.eligibility!.eligible_action;

    const isRenewal = eligibleAction === EEligibilityAction.earlyRenewal;

    let purchasePayload: IPurchase | null = null;

    purchasePayload = new Purchase(selectedOfferPrice, selectedOffer, rootOffer, cart.uuid);

    sendInteractEvent({
      action: EInteractEventTrackerAction.CHK_ORDER,
      attributes: {
        payment_method: paymentMethodSelected,
        new_payment_profile: Boolean(purchasePayload.payment_profile && purchasePayload.payment_profile.card_info),
        cookie_emp_u: getCookieEmpU()
      }
    });

    purchasePayload = null;

    try {
      let deliveryAddressId;
      if (selectedOffer && selectedOffer.is_address_required) {
        if (!rootGetters['App/Behavior/isDeliveryAddressValid']) {
          await dispatch('App/toggleLoader', false, root);
          return false;
        }

        const deliveryAddress = rootGetters['App/Customer/deliveryAddress'];
        if (deliveryAddress.acta_address_id) {
          deliveryAddressId = deliveryAddress.acta_address_id;
        } else {
          deliveryAddressId = await dispatch('App/Customer/saveDeliveryAddress', null, { root: true });
        }
      }

      const purchasePayload: IPurchase = new Purchase(selectedOfferPrice, selectedOffer, rootOffer, cart.uuid, deliveryAddressId);

      const purchaseRequest = isRenewal ? await renew(purchasePayload) : await buy({ ...purchasePayload, sales_channel: 'web-checkout' });
      const purchaseResponse: IPurchaseResponse = purchaseRequest.data;

      if (purchaseResponse.error && purchaseResponse.error.code) {
        throw { response: purchaseRequest };
      }

      purchasePayload.payment_profile && await dispatch('setPaymentPurchased', {
        card_last_digits: purchasePayload.payment_profile.card_last_digits
      });

      dispatch('App/Behavior/setPurchaseResponse', {
        id: isRenewal ? purchaseResponse.contract!.id : purchaseResponse.order!.id,
        method: paymentMethodSelected,
        amount: selectedMethod.cart_final_price,
      }, root);

      if (!isRenewal) {
        purchaseResponse.order && await dispatch('resolveOrderUpsell', purchaseResponse.order.id);
      } else {
        purchaseResponse.contract && await dispatch('getContractById', purchaseResponse.contract.id);
      }

      if (!isRenewal) {
        if (paymentMethodSelected === EPaymentMethod.creditCard) {
          pushOrderConfirmedToDatalayer(state.order, rootOffer, selectedOffer);
        } else if (paymentMethodSelected === EPaymentMethod.bankSlip) {
          pushOrderBankslipGeneratedToDatalayer(state.order, rootOffer, selectedOffer);
        }

        if (['wiretransfer', 'pix', 'bankslip'].includes(paymentMethodSelected)) {
          purshOrderAwaitingPaymentToDatalayer(state.order, rootOffer, selectedOffer);
        }
      }

      const { order, contract } = purchaseResponse;
      const status = (order && order.status) || '';

      await sendInteractEvent({
        action: EInteractEventTrackerAction.CHK_ORDER_SUCCESS,
        attributes: {
          return_code: 200,
          return_message: 'OK',
          new_payment_profile: Boolean(purchasePayload.payment_profile && purchasePayload.payment_profile.card_info),
          payment_method: paymentMethodSelected,
          cookie_emp_u: getCookieEmpU()
        }
      });

      await redirectToSuccessPage(
        status,
        eligibleAction,
        {
          ...(!isRenewal && order) && {
            slug: selectedOffer.slug,
            order_id: order.id,
          },
          ...(isRenewal && contract) && {
            contract_id: contract.id
          },
        }
      );
    } catch (error) {
      if (error.response) {
        const purchasePayload: IPurchase = new Purchase(selectedOfferPrice, selectedOffer, rootOffer, cart.uuid);
        const errorData = error.response.data;

        sendInteractEvent({
          action: error.response.status == 422
            ? EInteractEventTrackerAction.CHK_ORDER_PAYMENT_FAILED
            : EInteractEventTrackerAction.CHK_ORDER_SYSTEM_FAILED,
          attributes: {
            new_payment_profile: Boolean(purchasePayload.payment_profile && purchasePayload.payment_profile.card_info),
            payment_method: paymentMethodSelected,
            return_code: errorData.error.code,
            return_message: errorData.error.message,
            cookie_emp_u: getCookieEmpU()
          },
        });

        if (errorData.error && errorData.error.code) {
          const { code, origin, is_other_payment_methods_enabled, payment_details } = errorData.error as {
            code: number | string;
            origin: EErrorOrigin;
            is_other_payment_methods_enabled: boolean;
            payment_details: any;
          };

          const errorOrigin: EErrorOriginTransform = origin
            ? EErrorOriginTransform[origin]
            : EErrorOriginTransform.ECOMM_MS_PAYMENTS;

          const traceableError = getErrorByCodeAndType(code, [EErrorType.payment, EErrorType.eligibility], payment_details);

          if (traceableError) {
            addError(new TraceableError({
              ...traceableError,
              type: EErrorType.payment,
              scope: EErrorScope.payment,
              origin: errorOrigin,
              trigger: 'buy',
              exhibition: EErrorExhibition.modal,
              isOtherPaymentMethodsEnabled: is_other_payment_methods_enabled,
            }));
          }

          await dispatch('App/toggleLoader', false, root);

          return false;
        }

        const traceableError = getDefaultErrorByType(EErrorType.payment);

        addError(new TraceableError({
          ...traceableError,
          type: EErrorType.payment,
          code: error.response.status,
          scope: EErrorScope.payment,
          trigger: 'buy',
          severity: EErrorSeverity.critical,
          exhibition: EErrorExhibition.modal,
          isOtherPaymentMethodsEnabled: error.response.is_other_payment_methods_enabled,
        }));
      }

      dispatch('App/toggleLoader', false, root);
    } finally {
      commit('closeModalPayment');
      dispatch('setPaymentLoading', false);
    }

    return true;
  },
  async buyUpsell({ state, getters, commit, dispatch }: ActionContext<ICheckoutState, any>) {
    dispatch('setPaymentLoading', true);

    const order: IOrder = state.order;
    const upsell: IUpsell = state.upsell;
    const checkout: ICheckout = state.checkout;
    const cart: ICheckoutCart = checkout.cart;
    const rootOffer: ICheckoutCartOffer = getters.rootOffer;
    const selectedOffer: ICheckoutCartOffer = getters.selectedOffer;
    const selectedOfferPrice: ICheckoutCartOfferPrice = getters.selectedOfferPrice;
    const payment: Payment = selectedOffer.payment;
    const paymentMethodSelected: EPaymentMethod = payment.selectedPaymentMethod;
    const selectedMethod = payment.getPaymentMethod(paymentMethodSelected);
    const eligibleAction: EEligibilityAction = selectedOffer.eligibility!.eligible_action;

    const isRenewal = [EEligibilityAction.earlyRenewal, EEligibilityAction.lateRenewal].includes(eligibleAction);

    let purchasePayload = new UpsellPurchase(selectedOfferPrice, selectedOffer, rootOffer, cart.uuid, upsell.id);

    sendInteractEvent({
      action: EInteractEventTrackerAction.UPSELL_ORDER,
      attributes: {
        payment_method: selectedMethod.method,
        new_payment_profile: Boolean(purchasePayload.payment_profile && purchasePayload.payment_profile.card_info),
        cookie_emp_u: getCookieEmpU()
      },
    });

    try {
      const purchasePayload = new UpsellPurchase(selectedOfferPrice, selectedOffer, rootOffer, cart.uuid, upsell.id);
      const purchaseRequest = isRenewal
        ? await renewUpsell(order.id as number, purchasePayload, upsell.params)
        : await buyUpsellRemotely(order.id as number, { ...purchasePayload, sales_channel: 'web-checkout' }, upsell.params);
      const purchaseResponse: IPurchaseResponse = purchaseRequest.data;

      purchasePayload.payment_profile && await dispatch('setPaymentPurchased', {
        card_last_digits: purchasePayload.payment_profile.card_last_digits
      });

      dispatch('App/Behavior/setPurchaseResponse', {
        id: isRenewal ? purchaseResponse.contract!.id : purchaseResponse.order!.id,
        method: paymentMethodSelected,
        amount: selectedMethod.cart_final_price,
      }, root);

      commit('closeUpsellModal');

      purchaseResponse.order && await dispatch('resolveOrderUpsell', purchaseResponse.order.id);

      if (paymentMethodSelected === EPaymentMethod.creditCard) {
        pushOrderUpsellConfirmedToDatalayer(state.order, rootOffer, selectedOffer, !!upsell.custom_template);
      } else if (paymentMethodSelected === EPaymentMethod.bankSlip) {
        pushOrderUpsellBankslipGeneratedToDatalayer(state.order, rootOffer, selectedOffer, !!upsell.custom_template);
      }

      await sendInteractEvent({
        action: EInteractEventTrackerAction.UPSELL_ORDER_SUCCESS,
        attributes: {
          return_code: 200,
          return_message: 'OK',
          payment_method: paymentMethodSelected,
          new_payment_profile: Boolean(purchasePayload.payment_profile && purchasePayload.payment_profile.card_info),
          cookie_emp_u: getCookieEmpU()
        },
      });

      await redirectToSuccessPage(
        state.order.status,
        eligibleAction,
        {
          slug: upsell.offer_slug,
          order_id: state.order.id,
        }
      );
    } catch (error) {
      if (error.response) {
        let purchasePayload = new UpsellPurchase(selectedOfferPrice, selectedOffer, rootOffer, cart.uuid, upsell.id);
        const errorData = error.response.data;

        if (errorData.error && errorData.error.code) {
          const { code, origin, is_other_payment_methods_enabled, payment_details } = errorData.error as {
            code: number | string;
            origin: EErrorOrigin;
            is_other_payment_methods_enabled: boolean;
            payment_details: any;
          };

          const errorOrigin: EErrorOriginTransform = origin
            ? EErrorOriginTransform[origin]
            : EErrorOriginTransform.ECOMM_MS_PAYMENTS;

          const traceableError = getErrorByCodeAndType(code, [EErrorType.payment, EErrorType.eligibility], payment_details);

          if (traceableError) {
            addError(new TraceableError({
              ...traceableError,
              type: EErrorType.payment,
              scope: EErrorScope.payment,
              origin: errorOrigin,
              trigger: 'buyUpsell',
              exhibition: EErrorExhibition.modal,
              isOtherPaymentMethodsEnabled: is_other_payment_methods_enabled,
            }));
          }

          sendInteractEvent({
            action: error.response.status == 422
              ? EInteractEventTrackerAction.UPSELL_ORDER_PAYMENT_FAILED
              : EInteractEventTrackerAction.UPSELL_ORDER_SYSTEM_FAILED,
            attributes: {
              payment_method: paymentMethodSelected,
              new_payment_profile: Boolean(purchasePayload.payment_profile && purchasePayload.payment_profile.card_info),
              return_code: errorData.error.code,
              return_message: errorData.error.message,
              cookie_emp_u: getCookieEmpU()
            },
          });

          await dispatch('setPaymentLoading', false);

          return false;
        }

        const traceableError = getDefaultErrorByType(EErrorType.payment);

        addError(new TraceableError({
          ...traceableError,
          type: EErrorType.payment,
          code: error.response.status,
          scope: EErrorScope.payment,
          trigger: 'buyUpsell',
          severity: EErrorSeverity.critical,
          exhibition: EErrorExhibition.modal,
          isOtherPaymentMethodsEnabled: error.response.is_other_payment_methods_enabled,
        }));
      }
    } finally {
      commit('closeModalPayment');
      dispatch('setPaymentLoading', false);
    }

    return true;
  },
  async getOrderById({ commit }: ActionContext<ICheckoutState, any>, orderId: any) {
    try {
      const request = await fetchOrder(orderId);
      const response: IOrderDetailsResponse = request.data;

      if (response.error_code) throw new Error(String(response.error_code));

      const order = new Order(response.order);
      const contentLink = new TargetContentLink(response.target_content_link);

      commit('setOrder', order);
      commit('setTargetContentLink', contentLink);

      return { order, contentLink };
    } catch (error) {
      const traceableError = getDefaultErrorByType(EErrorType.loading);

      addError(new TraceableError({
        ...traceableError,
        type: EErrorType.loading,
        code: error.message ? error.message : error.response.status,
        scope: EErrorScope.global,
        trigger: 'getOrderById',
        severity: EErrorSeverity.critical,
        exhibition: EErrorExhibition.modal,
      }));
    }

    return { order: null, contentLink: null };
  },
  async getContractById({ commit }: ActionContext<ICheckoutState, any>, contractId: number) {
    try {
      const request = await getContractById(contractId);
      const response: IContractDetailsResponse = request.data;

      if (response.error) throw new Error(String(response.error.code));

      commit('setContract', response);

      return response;
    } catch (error) {
      const traceableError = getDefaultErrorByType(EErrorType.loading);

      addError(new TraceableError({
        ...traceableError,
        type: EErrorType.loading,
        code: error.message ? error.message : error.response.status,
        scope: EErrorScope.global,
        trigger: 'getContractById',
        severity: EErrorSeverity.critical,
        exhibition: EErrorExhibition.modal,
      }));
    }

    return {};
  },
  async getUpsellByOrderId({ commit }: ActionContext<ICheckoutState, any>, orderId: any) {
    try {
      const request = await fetchUpsell(orderId);

      if (request.data) {
        const upsell = new Upsell(request.data as IUpsell);

        commit('setUpsell', upsell);

        return upsell;
      }
    } catch (error) {
      const traceableError = getDefaultErrorByType(EErrorType.loading);

      addError(new TraceableError({
        ...traceableError,
        type: EErrorType.loading,
        code: error.response.status,
        scope: EErrorScope.global,
        trigger: 'getUpsellByOrderId',
        severity: EErrorSeverity.critical,
        exhibition: EErrorExhibition.none,
      }));
    }

    commit('setUpsell', null);

    return null;
  },
  async resolveOrder({ dispatch }: ActionContext<ICheckoutState, any>, orderId: any) {
    const { order, contentLink } = await dispatch('getOrderById', orderId);

    return { order, contentLink };
  },
  async resolveOrderUpsell({ dispatch }: ActionContext<ICheckoutState, any>, orderId: any) {
    const { order, contentLink } = await dispatch('getOrderById', orderId);
    const upsell = await dispatch('getUpsellByOrderId', orderId);

    return { order, contentLink, upsell };
  },
  selectOffer({ commit, dispatch }: ActionContext<ICheckoutState, any>, offerId: number) {
    commit('selectOffer', offerId);
    commit('App/Behavior/setOfferSelected', offerId, { root: true });

    dispatch('selectOfferPaymentStructure');
  },
  selectOfferPaymentMethod({ commit }: ActionContext<ICheckoutState, any>, options: { offer_id?: number, method: EPaymentMethod }) {
    commit('selectOfferPaymentMethod', options);
    commit('App/Behavior/setPaymentMethodSelected', options.method, { root: true });
  },
  selectOfferPaymentStructure({ commit, getters }: ActionContext<ICheckoutState, any>, priceId?: number | undefined | void) {
    const selectedOffer: Offer = getters['selectedOffer'];

    if (!selectedOffer) return;

    if (!priceId) {
      priceId = selectedOffer.selectOfferInitialPrice();
    } else {
      selectedOffer.selectOfferPrice(priceId);
    }

    commit('App/Behavior/setPriceSelected', priceId, { root: true });
  },
  selectOfferPaymentMethodType({ commit, getters }: ActionContext<ICheckoutState, any>, options: { offer_id?: number, type: EPaymentMethodTypes }) {
    const payment: Payment = getters['selectedOfferPayment'];
    const firstMethod = payment.getPaymentMethodsByType(options.type)[0].method;

    counter = 0;
    commit('selectOfferPaymentMethod', {
      ...options,
      method: firstMethod,
    });
    commit('App/Behavior/setPaymentMethodSelected', firstMethod, { root: true });
  },
  setPaymentPurchased({ getters }: ActionContext<ICheckoutState, any>, purchase: IPaymentPurchase) {
    const payment: Payment = getters['selectedOfferPayment'];

    payment.setCardPurchased(purchase);
  },
  setPaymentLoading({ getters }: ActionContext<ICheckoutState, any>, loading: boolean) {
    const payment: Payment = getters['selectedOfferPayment'];

    payment.setLoading(loading);
  },
  setOfferPaymentCard({ getters }: ActionContext<ICheckoutState, any>, card: any) {
    const payment: Payment = getters['selectedOfferPayment'];

    payment.setCard(card);
  },
  async getRefusalUpsell({ dispatch, commit, state }: ActionContext<ICheckoutState, any>, refusalId: number) {
    try {
      window.scrollTo(0, 0);
      dispatch('App/toggleLoader', true, root);

      const request = await fetchUpsell(state.order.id, { refusalId });

      if (request.data) {
        const upsell = new Upsell(request.data as IUpsell);

        commit('setUpsell', upsell);

        pushRefusalUpsellLoadedToDatalayer(state.order, upsell);

        return upsell;
      }
    } catch (error) {
      const traceableError = getDefaultErrorByType(EErrorType.loading);

      addError(new TraceableError({
        ...traceableError,
        type: EErrorType.loading,
        code: error.response.status,
        scope: EErrorScope.global,
        trigger: 'getRefusalUpsell',
        severity: EErrorSeverity.critical,
        exhibition: EErrorExhibition.none,
      }));
    } finally {
      dispatch('App/toggleLoader', false, root);
    }

    commit('setUpsell', null);

    return null;
  },
  async getRefusalUpsellCE(_: ActionContext<ICheckoutState, any>, { order, refusalId }: { order: any, refusalId: number}) {
    try {
      const request = await fetchUpsell(order.id, { refusalId });

      if (request.data) {
        const upsell = new Upsell(request.data as IUpsell);

        pushRefusalUpsellLoadedToDatalayer(order, upsell);

        return upsell;
      }
    } catch (error) {
      return null;
    }
  },
};
